import { memo } from "react";

import {
  type MoneyV2,
  type CountryCode,
} from "@shopify/hydrogen-react/storefront-api-types";

import { AddToCartButton } from "@components/Button/AddToCartButton.data";
import { Heading } from "@components/Heading";
import { ProductComboOption } from "@components/ProductComboOption";
import { Text } from "@components/Text";
import { Klarna } from "@react/sections/ProductHero/Klarna";
import {
  type CartProduct,
  type CartProductVariant,
  type ProductSelectedIndex,
  type ThemeMode,
} from "@root/tablescape/state";
import { tw } from "classnames";

const styles = {
  base: tw(
    "mb-2",
    "gap-x-2",
    "border-0",
    "border-t",
    "border-solid",
    "border-divider",
    "pt-6"
  ),
  optionTitle: tw("flex", "gap-x-2", "items-baseline", "mb-2", "text-primary"),
  productComboOptions: tw("flex", "flex-col", "gap-y-4"),
  addToCartButton: tw("mt-6", "md:mt-10"),
  klarna: tw("mt-4", "h-9"),
};

export type ProductComboOption = {
  isBundle?: boolean;
  price?: MoneyV2;
  products?: CartProduct[];
  title?: string;
};

type Props = {
  country: CountryCode;
  isIntelligemsEnabled: boolean;
  isKlarnaEnabled: boolean;
  label?: string;
  productComboOptionIds: string[];
  selectedIndex: ProductSelectedIndex;
  selectedProductComboOption?: ProductComboOption;
  selectedVariantsByIndex: Record<number, CartProductVariant[]>;
  theme: ThemeMode;
};

const ProductSelectorViewBase = ({
  country,
  isIntelligemsEnabled,
  isKlarnaEnabled,
  label = "Select Option",
  productComboOptionIds,
  selectedIndex,
  selectedProductComboOption,
  selectedVariantsByIndex,
  theme,
}: Props) => {
  return (
    <div className={styles.base}>
      <div className={styles.optionTitle}>
        <Heading as="h6">{label}:</Heading>
        {selectedProductComboOption?.title && (
          <Text as="body2">{selectedProductComboOption.title}</Text>
        )}
      </div>
      {productComboOptionIds.length > 0 && (
        <div className={styles.productComboOptions}>
          {productComboOptionIds.map((productComboOptionId, index) => (
            <ProductComboOption
              index={index}
              isActive={index === selectedIndex}
              key={`ProductComboOption_${productComboOptionId}`}
              productComboOptionId={productComboOptionId}
              selectedVariants={selectedVariantsByIndex[index] ?? []}
              theme={theme}
            />
          ))}
        </div>
      )}
      <div
        className={styles.addToCartButton}
        data-mtr-bundle-name={
          selectedProductComboOption?.isBundle
            ? selectedProductComboOption.title
            : ""
        }
        data-mtr-product-name={selectedProductComboOption?.title ?? ""}
        data-mtr-product-price={selectedProductComboOption?.price?.amount ?? ""}
        data-product-form-button="true"
      >
        <AddToCartButton
          isIntelligemsEnabled={isIntelligemsEnabled}
          products={selectedProductComboOption?.products}
          theme={theme}
          variants={selectedVariantsByIndex[selectedIndex]}
        />
      </div>
      {isKlarnaEnabled && selectedProductComboOption?.price && (
        <div className={styles.klarna}>
          <Klarna
            country={country}
            price={selectedProductComboOption.price}
            theme={theme}
          />
        </div>
      )}
    </div>
  );
};

export const ProductSelectorView = memo(ProductSelectorViewBase);
